import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { MotionConfig } from "framer-motion";
import { cn } from "@/shared/lib/utils";
import { useClientId } from "@/shared/hooks/useClientId";
import { useIntelGate, type IntelGate } from "@/features/intel-layer/hooks/useIntelGate";
import { useClientData } from "@/features/projects/contexts/ClientDataContext";
import { usePortfolioCounts } from "@/features/dashboard/hooks/usePortfolioCounts";
import { useExportsEnabled } from "@/shared/hooks/useExportsEnabled";
import { useDocumentTitle, buildPageTitle } from "@/shared/hooks/useDocumentTitle";
import {
  ClientDashboardChrome,
  type DashboardView,
} from "@/features/intel-layer/components/ClientDashboardChrome";
import { IntelChat } from "@/features/intel-layer/components/intelChat/IntelChat";
import { GeneralDashboardTab } from "@/features/intel-layer/components/GeneralDashboardTab";
import {
  SavedComponentsProvider,
  useSavedComponents,
} from "@/features/intel-layer/components/SavedComponentsProvider";
import { MyDashboardSheet } from "@/features/intel-layer/components/MyDashboardSheet";
import { RowDetailProvider } from "@/features/intel-layer/components/RowDetailSheet";
import { EvidenceProvider } from "@/features/intel-layer/components/EvidenceLightbox";

export default function ClientDashboardAgent() {
  const { clientId, loading } = useClientId();

  // Tenant-gating layer 1 of 3 — asks the DB tenant registry (via
  // fn_intelligence_layer_chat_enabled, the same RPC the edge fn gates on).
  // Every tenant gets the same surface; while the check is pending the
  // composer area stays empty, and disabled tenants get a "coming soon"
  // notice instead of a composer. Layers 2/3 (edge-fn gate, DB) still
  // hard-refuse direct calls.
  const chatGate = useIntelGate(clientId);

  // clientId resolves async (org claim / admin switcher) — render nothing for
  // that brief gap so neither surface variant flashes.
  if (loading) return null;

  // Resolved with no client (no org claim, or the lookup failed): say so
  // explicitly instead of leaving a permanently blank outlet.
  if (!clientId) {
    return (
      <div className="flex-1 flex items-center justify-center p-8 text-muted-foreground">
        We couldn't find an organization for this account.
      </div>
    );
  }

  return <IntelLayerDashboardSurface clientId={clientId} chatGate={chatGate} />;
}

function IntelLayerDashboardSurface({
  clientId,
  chatGate,
}: {
  clientId: string;
  chatGate: IntelGate;
}) {
  const { data: clientData } = useClientData();
  const { data: portfolio } = usePortfolioCounts(clientId);
  const { enabled: exportsEnabled } = useExportsEnabled(clientId);
  const clientName = clientData?.client?.name || "";
  useDocumentTitle(clientName ? buildPageTitle([clientName, "Dashboard"]) : null);

  // reducedMotion="user" makes every framer-motion component auto-skip
  // transform/layout animations (keeping opacity) under prefers-reduced-motion.
  // CSS-driven animation is handled by the global guard in index.css.
  return (
    <MotionConfig reducedMotion="user">
      {/* key={clientId} forces a remount when the route's client changes, so the
          localStorage-backed saved store re-reads from the new client's key
          instead of writing the prior client's items under it. */}
      <SavedComponentsProvider key={clientId} clientId={clientId}>
        <DashboardShell
          clientId={clientId}
          clientName={clientName}
          clientLogoUrl={clientData?.client?.logo_url}
          portfolio={portfolio}
          exportsEnabled={!!exportsEnabled}
          chatGate={chatGate}
        />
      </SavedComponentsProvider>
    </MotionConfig>
  );
}

interface DashboardShellProps {
  clientId: string;
  clientName: string;
  clientLogoUrl?: string | null;
  portfolio: ReturnType<typeof usePortfolioCounts>["data"];
  exportsEnabled: boolean;
  chatGate: IntelGate;
}

/** Inner shell (inside SavedComponentsProvider) so it can read the saved count
 *  for the chrome pill and own the My Dashboard sheet's open state.
 *
 *  Two in-page views behind one URL (/dashboard): the chat (Layer 2, landing)
 *  and the standing General dashboard (Layer 1). Both stay mounted once
 *  visited — hiding, not unmounting, preserves the chat transcript and the
 *  dashboard scroll position across switches. */
function DashboardShell({
  clientId,
  clientName,
  clientLogoUrl,
  portfolio,
  exportsEnabled,
  chatGate,
}: DashboardShellProps) {
  const { saved } = useSavedComponents();
  const [savedOpen, setSavedOpen] = useState(false);
  const [view, setView] = useState<DashboardView>("chat");
  const [generalVisited, setGeneralVisited] = useState(false);

  // The per-store view lives on the general surface (?store=<number>, owned by
  // GeneralDashboardTab). A store deep link — or a store param appearing while
  // the chat is up — forces the general tab active so the store view is
  // visible. Chat/general switching itself stays OUT of the URL.
  const [searchParams] = useSearchParams();
  const storeParam = searchParams.get("store");
  useEffect(() => {
    if (!storeParam) return;
    setView("general");
    setGeneralVisited(true);
  }, [storeParam]);

  const handleViewChange = (next: DashboardView) => {
    setView(next);
    if (next === "general") setGeneralVisited(true);
  };

  return (
    <div className="flex-1 flex flex-col h-screen overflow-hidden bg-background">
      {/* RowDetail + Evidence providers wrap BOTH the live surface and the
          My Dashboard sheet. The sheet is a sibling of the surfaces, so
          saved tables/charts it re-renders must share these providers or
          useRowDetail/useEvidence throws. */}
      <EvidenceProvider>
        <RowDetailProvider>
          <ClientDashboardChrome
            clientId={clientId}
            clientName={clientName}
            clientLogoUrl={clientLogoUrl}
            portfolio={portfolio}
            exportsEnabled={exportsEnabled}
            savedCount={saved.length}
            onOpenSaved={() => setSavedOpen(true)}
            view={view}
            onViewChange={handleViewChange}
          />
          <div className={cn("flex-1 flex flex-col min-h-0", view !== "chat" && "hidden")}>
            <IntelChat clientId={clientId} clientName={clientName} chatGate={chatGate} />
          </div>
          {generalVisited ? (
            <div
              className={cn("flex-1 min-h-0 overflow-y-auto", view !== "general" && "hidden")}
            >
              <GeneralDashboardTab clientId={clientId} />
            </div>
          ) : null}
          <MyDashboardSheet open={savedOpen} onOpenChange={setSavedOpen} />
        </RowDetailProvider>
      </EvidenceProvider>
    </div>
  );
}
